import {useState, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {useLiveKitRoom} from './useLiveKitRoom'
import {getOrCreateUserId} from '../lib/uuid'

interface UseRoomPageOptions {
  roomId: string
}

interface UseRoomPageResult {
  state: RoomPageState
  onJoin: (username: string, audioEnabled: boolean, videoEnabled: boolean) => void
  onLeave: () => void
}

interface JoinData {
  username: string
  audioEnabled: boolean
  videoEnabled: boolean
}

export type RoomPageState =
  | {phase: 'lobby'}
  | {phase: 'loading'}
  | {phase: 'error'; errorMessage: string}
  | {phase: 'ready'; token: string; joinData: JoinData}

/**
 * Encapsulates room page state: lobby → loading → ready/error.
 * Exposes a discriminated union `state` for type-safe rendering.
 */
export function useRoomPage({roomId}: UseRoomPageOptions): UseRoomPageResult {
  const navigate = useNavigate()
  const [userId] = useState<string>(() => getOrCreateUserId())
  const [joinData, setJoinData] = useState<JoinData | null>(null)

  const {connection, connect} = useLiveKitRoom({userId})

  const onJoin = useCallback(
    (username: string, audioEnabled: boolean, videoEnabled: boolean) => {
      setJoinData({username, audioEnabled, videoEnabled})
      connect(roomId, username)
    },
    [connect, roomId],
  )

  const onLeave = useCallback(() => {
    navigate('/')
  }, [navigate])

  const state: RoomPageState = (() => {
    if (!joinData) return {phase: 'lobby'}

    switch (connection.status) {
      case 'idle':
      case 'connecting':
        return {phase: 'loading'}
      case 'error':
        return {phase: 'error', errorMessage: connection.error}
      case 'connected':
        return {phase: 'ready', token: connection.token, joinData}
    }
  })()

  return {state, onJoin, onLeave}
}
